import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createRoute } from "@/store/slices/routesSlice";
import { INDIAN_STATES } from "@/utils/constants";
import Modal from "../common/Modal";
import TextInput from "../common/TextInput";
import TextArea from "../common/TextArea";
import SelectDropdown from "../common/SelectDropdown";
import GPXUpload from "../common/GPXUpload";
import Button from "../common/Button";

const DIFFICULTY_OPTIONS = [
  { value: "", label: "Select Difficulty" },
  { value: "Easy", label: "Easy" },
  { value: "Medium", label: "Medium" },
  { value: "Hard", label: "Hard" },
];

const RouteFormModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { submitLoading } = useSelector((state) => state.routes);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    state_code: "",
    difficulty: "",
    start_point: "",
    end_point: "",
  });
  const [gpxFile, setGpxFile] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (fieldName) => (e) => {
    setFormData((prev) => ({ ...prev, [fieldName]: e.target.value }));
  };

  const handleGPXUpload = (file) => {
    setGpxFile(file);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      state_code: "",
      difficulty: "",
      start_point: "",
      end_point: "",
    });
    setGpxFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.warn("Please enter a route name");
      return;
    }
    if (!formData.description.trim()) {
      toast.warn("Please enter a description");
      return;
    }
    if (!formData.state_code) {
      toast.warn("Please select a state");
      return;
    }
    if (!formData.difficulty) {
      toast.warn("Please select a difficulty level");
      return;
    }
    if (!formData.start_point.trim()) {
      toast.warn("Please enter a start point");
      return;
    }
    if (!formData.end_point.trim()) {
      toast.warn("Please enter an end point");
      return;
    }
    if (!gpxFile) {
      toast.warn("Please upload a GPX file");
      return;
    }

    // Create FormData
    const data = new FormData();
    data.append("gpxFile", gpxFile);
    data.append("name", formData.name.trim());
    data.append("description", formData.description.trim());
    data.append("state_code", formData.state_code);
    data.append("difficulty", formData.difficulty);
    data.append("start_point", formData.start_point.trim());
    data.append("end_point", formData.end_point.trim());

    try {
      const result = await dispatch(createRoute(data)).unwrap();
      toast.success(result.message || "Route submitted successfully!");
      resetForm();
      onClose();
    } catch (err) {
      toast.error(err || "Failed to submit route");
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Upload New Route"
      showCloseButton={false}
    >
      <form
        onSubmit={handleSubmit}
        className="space-y-4 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto px-1"
      >
        <TextInput
          label="Route Name *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g., Mumbai Coastal Circuit"
          required
        />

        <TextArea
          label="Description *"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Brief description of the route"
          rows={3}
          required
        />

        <SelectDropdown
          label="State *"
          name="state_code"
          options={INDIAN_STATES.filter((s) => s.code).map((s) => ({
            value: s.code,
            label: s.name,
          }))}
          value={formData.state_code}
          onChange={handleSelectChange("state_code")}
          placeholder="Select a State"
          required
        />

        <SelectDropdown
          label="Difficulty *"
          name="difficulty"
          options={DIFFICULTY_OPTIONS}
          value={formData.difficulty}
          onChange={handleSelectChange("difficulty")}
          required
        />

        <TextInput
          label="Start Point *"
          name="start_point"
          value={formData.start_point}
          onChange={handleChange}
          placeholder="e.g., Marine Drive"
          required
        />

        <TextInput
          label="End Point *"
          name="end_point"
          value={formData.end_point}
          onChange={handleChange}
          placeholder="e.g., Gateway of India"
          required
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            GPX File Upload *
          </label>
          <GPXUpload onUpload={handleGPXUpload} />
          {gpxFile && (
            <p className="text-sm text-green-600 font-medium dark:text-green-400">
              ✓ GPX file selected: {gpxFile.name}
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={submitLoading}
            disabled={submitLoading}
          >
            {submitLoading ? "Submitting..." : "Submit Route"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RouteFormModal;
